/**
 * @fileoverview Trial Store - Zustand store for trial/subscription state
 * 
 * Manages trial status, limits, and premium feature access across the app.
 * 
 * @module stores/useTrialStore
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { trialApi } from '@/api/trial.api';

// ============================================================================
// TRIAL CONFIGURATION
// ============================================================================

const DEFAULT_TRIAL_CONFIG = {
  maxTables: 9999,  // Unlimited during trial
  maxMenuItems: 9999,  // Unlimited during trial
  trialDurationDays: 7,  // the backend decides (TRIAL_DAYS_TN / TRIAL_DAYS_INTL, 7 by default); informative only
  // ALL features available in trial (full access)
  freeFeatures: [
    'tables', 
    'menu', 
    'category',
    'orders', 
    'order',
    'place_order',
    'getting-started',
    'subscription',
    'restaurants',
    'dashboard',
    'statistics', 
    'reports', 
    'employees',
    'employee', 
    'multi_restaurant', 
    'export', 
    'inventory', 
    'marketing',
    'MarketingPage',
    'costs',
    'bookings',
    'clickAndCollect',
    'click-and-collect',
    'activity-log',
    'notes',
    'notifications',
    'table-management',
    'settings'
  ]
};

// ============================================================================
// TRIAL STORE
// ============================================================================

export const useTrialStore = create(
  persist(
    (set, get) => ({
      // State
      trialStatus: null,
      isLoading: false,
      error: null,
      lastFetched: null,

      // Computed getters
      isTrial: () => get().trialStatus?.is_trial ?? false,
      isExpired: () => get().trialStatus?.is_expired ?? false,
      daysRemaining: () => get().trialStatus?.days_remaining ?? 0,
      
      getLimits: () => get().trialStatus?.limits ?? {
        tables: { current: 0, max: 2, can_add: true },
        menu_items: { current: 0, max: 2, can_add: true }
      },

      // Actions
      fetchTrialStatus: async () => {
        set({ isLoading: true, error: null });
        try {
          const status = await trialApi.getTrialStatus();
          set({ 
            trialStatus: status, 
            isLoading: false,
            lastFetched: Date.now()
          });
          return status;
        } catch (error) {
          set({ 
            error: error.message, 
            isLoading: false
          });
          return null;
        }
      },

      canAccessFeature: (feature) => {
        const state = get();
        const { trialStatus } = state;
        
        // If not trial or no status, allow access
        if (!trialStatus?.is_trial) return true;
        
        // Check if feature is in free features list
        const freeFeatures = trialStatus.free_features || DEFAULT_TRIAL_CONFIG.freeFeatures;
        return freeFeatures.includes(feature);
      },

      reset: () => set({ 
        trialStatus: null, 
        isLoading: false, 
        error: null,
        lastFetched: null
      })
    }),
    {
      name: 'trial-storage',
      partialize: (state) => ({ 
        trialStatus: state.trialStatus,
        lastFetched: state.lastFetched
      }),
    }
  )
);

// ============================================================================
// CONVENIENCE HOOK
// ============================================================================

/**
 * Hook for easy access to trial state
 * @returns {Object} Trial state and actions
 */
export const useTrial = () => {
  const store = useTrialStore();
  return {
    fetchTrialStatus: store.fetchTrialStatus,
  };
};

export default useTrialStore;
